import type { PortableTextMarkComponent } from "@portabletext/react";
import { PortableText } from "@portabletext/react";
import React from "react";

import { wrapTextNodes } from "@sorcery/utils/client/sanity";

import { ExternalLink } from "../../../atoms/link/ExternalLink";
import { H2 } from "../../../atoms/typography/H2";
import { H3 } from "../../../atoms/typography/H3";
import { P } from "../../../atoms/typography/P";
import { SanityDamageGrid } from "../embeds/SanityDamageGrid";
import { SanityDivider } from "../embeds/SanityDivider";
import { SanityOrientedImage } from "../embeds/SanityOrientedImage";
import { SanityVideo } from "../embeds/SanityVideo";

/**
 * This component uses Portable Text to render a _document's content body.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */
export const CMSContent = <
  T extends {
    children?: {
      marks?: string[];
      text?: string;
      _type: "span";
      _key: string;
    }[];
    style?: "normal" | "h4" | "h3";
    listItem?: "bullet" | "number";
    markDefs?: {
      _type: string;
      _key: string;
      href?: string;
      blank?: boolean;
      reference?: {
        _ref: string;
        _type: "reference";
        _weak?: boolean;
      };
    }[];
    level?: number;
    _type: "block" | "damageGrid" | "divider" | "orientedImage" | "youtube";
    _key: string;
  }[],
>({
  content,
  config,
  marks,
  renderer,
}: {
  content: T;
  config: {
    dataset: string;
    projectId: string;
  };
  marks?: Record<string, PortableTextMarkComponent<any> | undefined>;
  renderer?: (raw: string) => React.ReactNode;
}) => {
  return (
    <PortableText
      value={content}
      components={{
        // Custom components defined in our sanity schema
        types: {
          divider: SanityDivider,
          orientedImage: (args) =>
            SanityOrientedImage({
              ...args,
              dataset: config.dataset,
              projectId: config.projectId,
            }),
          youtube: SanityVideo,
          damageGrid: SanityDamageGrid,
        },
        // For all other nodes - this ensures all text nodes have a persistent parent node
        // to handle the google translate chrome extension bug
        // https://github.com/facebook/react/issues/11538
        // https://martijnhols.nl/gists/everything-about-google-translate-crashing-react
        block: {
          /* title */
          h3: ({ children }) => (
            //  lol i know...
            <H2 className="mt-12 text-big-text-color">{wrapTextNodes(children)}</H2>
          ),
          /* subtitle */
          h4: ({ children }) => (
            //  lol i know...
            <H3 className="mt-6 text-big-text-color">{wrapTextNodes(children)}</H3>
          ),
          /* regular text */
          normal: ({ children }) => {
            return (
              <P>
                {renderer
                  ? React.Children.map(children, (child) =>
                      typeof child === "string" ? renderer(child) : child,
                    )
                  : wrapTextNodes(children)}
              </P>
            );
          },
        },
        list: {
          bullet: ({ children }) => (
            <ul className="mb-4 ml-8 list-outside list-disc [&>li>ul]:mb-0 [&>li]:my-1 [&>li>ul]:list-[circle] [&>li>ul>li>ul]:list-[square]">
              {children}
            </ul>
          ),
          number: ({ children }) => (
            <ol className="mb-4 ml-8 list-outside list-decimal [&>li>ol]:mb-0 [&>li]:my-1 [&>li>ol]:list-[lower-alpha] [&>li>ol>li>ol]:list-[lower-roman]">
              {children}
            </ol>
          ),
        },
        listItem: ({ children }) => (
          <li>
            {renderer
              ? React.Children.map(children, (child) =>
                  typeof child === "string" ? renderer(child) : child,
                )
              : wrapTextNodes(children)}
          </li>
        ),
        // Custom markup on external and internal cms links
        marks: {
          link: ({ value, children }) => {
            const { blank, href } = value;
            // default historical links that didnt have this field to open new tabs
            return (
              <ExternalLink
                href={href as string}
                color="primary"
                target={blank || typeof blank === "undefined" ? "_blank" : undefined}
                rel="noopener noreferrer"
              >
                {wrapTextNodes(children)}
              </ExternalLink>
            );
          },

          ...(marks ?? {}),
        },
      }}
    />
  );
};
